/**
 * Trash page.
 * Shows deleted documents that can be restored.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useProjects, useDeletedProjects, useRestoreProject, useHardDeleteProject } from "../hooks/useProjects";
import { useDocuments, useRestoreDocument, useHardDeleteDocument } from "../hooks/useDocuments";
import { isProjectNameTaken } from "../lib/projectErrors";
import { PageHeader } from "../components/layout/PageHeader";
import { KiiConfirmDialog } from "@/components/kii/kii-confirm-dialog";
import { Button } from "@/components/ui/button";
import {
  Trash2,
  FileText,
  RotateCcw,
  Search,
  Clock,
  FolderKanban,
} from "lucide-react";

export function Trash() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { activeWorkspace } = useWorkspace();
  const { data: projectsData } = useProjects();
  const { data: deletedProjectsData, isLoading: isLoadingDeletedProjects } = useDeletedProjects();
  const restoreDocument = useRestoreDocument();
  const restoreProjectMutation = useRestoreProject();
  const hardDeleteDocument = useHardDeleteDocument();
  const hardDeleteProject = useHardDeleteProject();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [hardDeleteDocTarget, setHardDeleteDocTarget] = useState<{projectId: string; documentId: string; name: string} | null>(null);
  const [hardDeleteProjectTarget, setHardDeleteProjectTarget] = useState<{projectId: string; name: string} | null>(null);
  // The message of the last failed document restore or permanent delete.
  const [documentError, setDocumentError] = useState<string | null>(null);

  const projects = projectsData?.projects || [];

  // Get deleted documents for selected project
  // workspaceId is now auto-injected from the store
  const { data: deletedDocsData, isLoading } = useDocuments(
    selectedProject || undefined,
    { deleted: true }
  );

  const deletedDocs = deletedDocsData?.documents || [];
  const filteredDocs = deletedDocs.filter((d) =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRestore = async (projectId: string, documentId: string) => {
    try {
      await restoreDocument.mutateAsync({ projectId, documentId });
      setDocumentError(null);
    } catch (error) {
      console.error("Failed to restore document:", error);
      setDocumentError(t("trash.restoreDocumentError"));
    }
  };

  const handleHardDeleteDocument = async () => {
    if (!hardDeleteDocTarget) return;
    try {
      await hardDeleteDocument.mutateAsync({
        projectId: hardDeleteDocTarget.projectId,
        documentId: hardDeleteDocTarget.documentId,
      });
      setHardDeleteDocTarget(null);
      setDocumentError(null);
    } catch (error) {
      console.error("Failed to permanently delete document:", error);
      setDocumentError(t("trash.deleteDocumentError"));
    }
  };

  const handleHardDeleteProject = async () => {
    if (!hardDeleteProjectTarget) return;
    try {
      await hardDeleteProject.mutateAsync(hardDeleteProjectTarget.projectId);
      setHardDeleteProjectTarget(null);
    } catch (error) {
      console.error("Failed to permanently delete project:", error);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("trash.title")}
        description={t("trash.description")}
        breadcrumbs={[{ label: t("trash.title") }]}
      />

      {documentError && (
        <p role="alert" className="mb-4 text-sm text-destructive">
          {documentError}
        </p>
      )}

      {!activeWorkspace ? (
        <div className="bento-card bento-surface border border-border text-center">
          <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            {t("trash.noWorkspace")}
          </h2>
          <p className="text-muted-foreground">
            {t("trash.noWorkspaceMessage")}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
        {/* Deleted Projects Section */}
        {isLoadingDeletedProjects ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (deletedProjectsData?.projects?.length ?? 0) > 0 ? (
          <div>
            <h2 className="text-lg font-medium text-foreground mb-3">
              {t("trash.deletedProjects")}
            </h2>
            {isProjectNameTaken(restoreProjectMutation.error) && (
              <p role="alert" className="mb-3 text-sm text-destructive">
                {t("projects.nameTaken")}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {deletedProjectsData!.projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-card rounded-lg border border-border p-4 flex flex-col"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <FolderKanban className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-foreground truncate">
                        {project.name}
                      </h3>
                      {project.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-auto pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(project.deleted_at, "long-date")}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                        onClick={() => setHardDeleteProjectTarget({
                          projectId: project.id,
                          name: project.name,
                        })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <button
                        onClick={() => restoreProjectMutation.mutate(project.id)}
                        disabled={restoreProjectMutation.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RotateCcw className="h-4 w-4" />
                        {restoreProjectMutation.isPending
                          ? t("trash.restoringProject")
                          : t("trash.restoreProject")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Deleted Documents Section */}
        <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-6">
          {/* Project Selector - horizontal scroll on mobile, sidebar on desktop */}
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="p-3 bg-muted border-b border-border">
                <h3 className="text-sm font-medium text-foreground">{t("trash.projects")}</h3>
              </div>
              {/* Mobile: horizontal scroll */}
              <div className="lg:hidden overflow-x-auto">
                {projects.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">{t("trash.noProjects")}</p>
                ) : (
                  <div className="flex gap-2 p-3">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => setSelectedProject(project.id)}
                        className={`shrink-0 px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                          selectedProject === project.id
                            ? "bg-nara-orange/15 text-nara-orange font-medium"
                            : "bg-muted text-foreground hover:bg-accent"
                        }`}
                      >
                        <FolderKanban className="h-4 w-4" />
                        <span className="truncate max-w-[150px]">{project.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Desktop: vertical list */}
              <div className="hidden lg:block max-h-96 overflow-y-auto">
                {projects.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">{t("trash.noProjects")}</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {projects.map((project) => (
                      <li key={project.id}>
                        <button
                          onClick={() => setSelectedProject(project.id)}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent transition-colors ${
                            selectedProject === project.id
                              ? "bg-primary/10 border-l-2 border-primary"
                              : ""
                          }`}
                        >
                          <FolderKanban
                            className={`h-4 w-4 shrink-0 ${
                              selectedProject === project.id
                                ? "text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                          <span
                            className={`text-sm truncate ${
                              selectedProject === project.id
                                ? "text-primary font-medium"
                                : "text-foreground"
                            }`}
                          >
                            {project.name}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Deleted Documents */}
          <div className="lg:col-span-3">
            {!selectedProject ? (
              <div className="bento-card bento-surface border border-border text-center">
                <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-foreground mb-2">
                  {t("trash.selectProject")}
                </h3>
                <p className="text-muted-foreground">
                  {t("trash.selectProjectMessage")}
                </p>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={t("trash.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Documents List */}
                {isLoading ? (
                  <div className="space-y-2 lg:space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-14 lg:h-16 bg-muted rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                ) : filteredDocs.length > 0 ? (
                  <div className="bg-card rounded-lg border border-border overflow-hidden">
                    {/* Mobile: Card list */}
                    <div className="sm:hidden divide-y divide-border">
                      {filteredDocs.map((doc) => (
                        <div key={doc.id} className="p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                              <div className="min-w-0">
                                <span className="font-medium text-foreground block truncate">
                                  {doc.name}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {t("trash.deletedOn", { date: formatDate(doc.deleted_at, "long-date") })}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                                onClick={() => setHardDeleteDocTarget({
                                  projectId: doc.project_id,
                                  documentId: doc.id,
                                  name: doc.name || "Untitled",
                                })}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <button
                                onClick={() =>
                                  handleRestore(selectedProject!, doc.id)
                                }
                                disabled={restoreDocument.isPending}
                                className="shrink-0 p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                              >
                                <RotateCcw className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop: Table */}
                    <table className="w-full hidden sm:table">
                      <thead className="bg-muted border-b border-border">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                            {t("trash.tableHeader.name")}
                          </th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-foreground hidden md:table-cell">
                            {t("trash.tableHeader.deleted")}
                          </th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-foreground w-24">
                            {t("trash.tableHeader.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredDocs.map((doc) => (
                          <tr key={doc.id} className="hover:bg-accent">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                                <span className="font-medium text-foreground truncate">
                                  {doc.name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {formatDate(doc.deleted_at, "long-date")}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                                  onClick={() => setHardDeleteDocTarget({
                                    projectId: doc.project_id,
                                    documentId: doc.id,
                                    name: doc.name || "Untitled",
                                  })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                <button
                                  onClick={() =>
                                    handleRestore(selectedProject!, doc.id)
                                  }
                                  disabled={restoreDocument.isPending}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  {t("trash.restore")}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bento-card bento-surface border border-border text-center">
                    <Trash2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-medium text-foreground mb-2">
                      {searchTerm ? t("trash.noMatching") : t("trash.empty")}
                    </h3>
                    <p className="text-muted-foreground">
                      {searchTerm
                        ? t("trash.adjustSearch")
                        : t("trash.emptyMessage")}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Permanent Delete Document Confirmation */}
      <KiiConfirmDialog
        open={!!hardDeleteDocTarget}
        onOpenChange={(open) => !open && setHardDeleteDocTarget(null)}
        onConfirm={handleHardDeleteDocument}
        title={t("trash.permanentDeleteTitle")}
        description={t("trash.permanentDeleteDescription", { name: hardDeleteDocTarget?.name || "" })}
        confirmText={t("trash.permanentDeleteConfirm")}
        cancelText={t("trash.cancel")}
        variant="destructive"
      />

      {/* Permanent Delete Project Confirmation */}
      <KiiConfirmDialog
        open={!!hardDeleteProjectTarget}
        onOpenChange={(open) => !open && setHardDeleteProjectTarget(null)}
        onConfirm={handleHardDeleteProject}
        title={t("trash.permanentDeleteProjectTitle")}
        description={t("trash.permanentDeleteProjectDescription", { name: hardDeleteProjectTarget?.name || "" })}
        confirmText={t("trash.permanentDeleteConfirm")}
        cancelText={t("trash.cancel")}
        variant="destructive"
      />
    </div>
  );
}
